import { motion } from "framer-motion";
import {
  ScanLine,
  Receipt,
  Sparkles,
  CheckCircle2,
  Calendar,
  Tag,
  PackageX,
} from "lucide-react";

/**
 * Hero micro-animation: a paper receipt slides in from the right, a scanning
 * line (gold) sweeps over it, and the right side morphs into a structured
 * digital table of recognized products.
 * Pure Framer Motion, CSS-only assets — no external GIF/Lottie required.
 */
export default function HeroScanAnimation() {
  return (
    <div
      data-testid="hero-scan-animation"
      className="relative w-full max-w-md mx-auto aspect-[4/5] rounded-3xl bg-gradient-to-br from-[#E8F3EC] via-[#F0F5E8] to-[#FDF6E3] border border-[#D8E3CC] overflow-hidden shadow-xl"
    >
      {/* Soft grain texture overlay */}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none mix-blend-multiply"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 30%, #2E4A3B 0%, transparent 50%), radial-gradient(circle at 80% 70%, #C65D47 0%, transparent 50%)",
        }}
      />

      {/* Phone frame with screen */}
      <div className="absolute inset-x-6 sm:inset-x-10 top-8 bottom-8 flex flex-col gap-3">
        {/* Receipt phase */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center"
          initial={{ opacity: 1 }}
          animate={{ opacity: [1, 1, 0, 0] }}
          transition={{ duration: 6, repeat: Infinity, times: [0, 0.4, 0.5, 1] }}
        >
          <PaperReceipt />
        </motion.div>

        {/* Scanning line + transition flash */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0, 1, 1, 0, 0] }}
          transition={{ duration: 6, repeat: Infinity, times: [0, 0.3, 0.35, 0.55, 0.6, 1] }}
        >
          <motion.div
            className="absolute left-2 right-2 h-[2px] rounded-full bg-gradient-to-r from-transparent via-[#D19B34] to-transparent"
            initial={{ top: "10%", boxShadow: "0 0 24px 4px #D19B34" }}
            animate={{ top: ["10%", "90%", "10%"] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          />
        </motion.div>

        {/* Result phase: digital table */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0, 0, 1, 1, 0] }}
          transition={{ duration: 6, repeat: Infinity, times: [0, 0.5, 0.55, 0.6, 0.95, 1] }}
        >
          <DigitalTable />
        </motion.div>
      </div>

      {/* Floating "AI" sparkle badge */}
      <motion.div
        className="absolute top-6 right-6 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur-sm border border-[#D8E3CC] shadow-sm text-[11px] font-semibold text-[#2E4A3B]"
        initial={{ y: -8, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.6 }}
      >
        <Sparkles size={12} className="text-[#D19B34]" />
        AI a lavoro
      </motion.div>
    </div>
  );
}

function PaperReceipt() {
  return (
    <motion.div
      initial={{ opacity: 0, rotate: -3 }}
      animate={{ opacity: 1, rotate: -2 }}
      transition={{ duration: 0.6 }}
      className="relative w-[68%] max-w-[230px] aspect-[2/3] bg-white shadow-2xl"
      style={{
        clipPath:
          "polygon(0 0, 100% 0, 100% 96%, 95% 100%, 90% 96%, 85% 100%, 80% 96%, 75% 100%, 70% 96%, 65% 100%, 60% 96%, 55% 100%, 50% 96%, 45% 100%, 40% 96%, 35% 100%, 30% 96%, 25% 100%, 20% 96%, 15% 100%, 10% 96%, 5% 100%, 0 96%)",
      }}
    >
      <div className="p-5 space-y-2 font-mono text-[9px] text-[#1A1C19]">
        <div className="text-center font-semibold pb-2 border-b border-dashed border-[#1A1C19]/30">
          ALIMENTARI ROSSI
          <div className="text-[8px] font-normal text-[#5C5F5A]">Via Roma 14</div>
        </div>
        <ReceiptLine name="POMODORI PACHINO" qty="0,540 kg" price="3,80" />
        <ReceiptLine name="PARMIGIANO 24M" qty="0,180 kg" price="6,75" />
        <ReceiptLine name="PROSCIUTTO CRUDO" qty="0,250 kg" price="9,40" />
        <ReceiptLine name="OLIVE TAGGIASCHE" qty="1 pz" price="4,50" />
        <ReceiptLine name="PANE INTEGRALE" qty="1 pz" price="2,80" />
        <div className="pt-2 border-t border-dashed border-[#1A1C19]/30 flex justify-between font-semibold">
          <span>TOTALE</span>
          <span>€ 27,25</span>
        </div>
      </div>
    </motion.div>
  );
}

function ReceiptLine({ name, qty, price }) {
  return (
    <div className="flex justify-between gap-1 leading-tight">
      <div className="min-w-0 flex-1 truncate">
        <div className="truncate">{name}</div>
        <div className="text-[8px] text-[#5C5F5A]">{qty}</div>
      </div>
      <div className="font-semibold whitespace-nowrap">€ {price}</div>
    </div>
  );
}

function DigitalTable() {
  const rows = [
    { name: "Pomodori Pachino", icon: Tag, qty: "0,540 kg", price: "€ 3,80", expires: "+3g", tone: "fresh" },
    { name: "Parmigiano 24M", icon: Tag, qty: "0,180 kg", price: "€ 6,75", expires: "+30g", tone: "neutral" },
    { name: "Prosciutto Crudo", icon: Tag, qty: "0,250 kg", price: "€ 9,40", expires: "+7g", tone: "fresh" },
    { name: "Olive Taggiasche", icon: PackageX, qty: "1 pz", price: "€ 4,50", expires: "+6m", tone: "neutral" },
  ];
  return (
    <motion.div
      initial={{ y: 16, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="w-full max-w-[260px] bg-white rounded-2xl shadow-2xl border border-[#E5E3DB] overflow-hidden"
    >
      <div className="px-3 py-2.5 bg-[#2E4A3B] text-white flex items-center gap-2">
        <ScanLine size={13} className="text-[#D19B34]" />
        <span className="text-[11px] font-semibold tracking-tight">Magazzino aggiornato</span>
        <CheckCircle2 size={13} className="ml-auto text-[#A8D4B8]" />
      </div>
      <div className="divide-y divide-[#F0EFE9]">
        {rows.map((r, i) => (
          <motion.div
            key={r.name}
            initial={{ x: -8, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.1 + i * 0.08, duration: 0.35 }}
            className="flex items-center gap-2 px-3 py-2"
          >
            <div className="h-7 w-7 rounded-lg bg-[#E8F3EC] text-[#2E4A3B] flex items-center justify-center flex-shrink-0">
              <r.icon size={12} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-semibold text-[#1A1C19] truncate">{r.name}</div>
              <div className="text-[9px] text-[#5C5F5A] flex items-center gap-1.5">
                <span>{r.qty}</span>
                <span className="text-[#C65D47]">·</span>
                <span className="font-mono">{r.price}</span>
              </div>
            </div>
            <div
              className={
                "text-[9px] font-mono font-semibold px-1.5 py-0.5 rounded border " +
                (r.tone === "fresh"
                  ? "bg-[#FDF6E3] text-[#8B6518] border-[#F2DEB0]"
                  : "bg-[#E8F3EC] text-[#2B5A3F] border-[#C5E1D1]")
              }
            >
              <Calendar size={9} className="inline mr-0.5 -mt-0.5" />
              {r.expires}
            </div>
          </motion.div>
        ))}
      </div>
      <div className="px-3 py-2 bg-[#FAF9F6] flex items-center gap-1.5 text-[10px] text-[#5C5F5A]">
        <Receipt size={10} className="text-[#2E4A3B]" />
        <span>4 prodotti riconosciuti in 2,1 secondi</span>
      </div>
    </motion.div>
  );
}
